import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRuler, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import QuiltSetup from './QuiltSetup';

const Step1 = ({ 
    quiltDimensions, 
    handleDimensionsChange, 
    isQuiltDimensionsValid, 
    gridSize, 
    requiredImages, 
    generated, 
    uploadedImages, 
    nextStep 
}) => {
    const [showWarning, setShowWarning] = useState(false);

    // Determine if all images are already uploaded
    const allImagesUploaded = uploadedImages.length >= requiredImages && requiredImages > 0;

    // Generate tile colors for the visual grid
    const generateTileColors = () => {
        if (!quiltDimensions.columns || !quiltDimensions.rows || 
            quiltDimensions.columns <= 0 || quiltDimensions.rows <= 0) {
            return [];
        }
        
        const colors = ['bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-red-100', 'bg-purple-100', 'bg-pink-100'];
        const tiles = [];
        
        for (let i = 0; i < quiltDimensions.rows; i++) {
            for (let j = 0; j < quiltDimensions.columns; j++) {
                const colorIndex = (i + j) % colors.length;
                tiles.push(
                    <div 
                        key={`${i}-${j}`} 
                        className={`border border-gray-300 ${colors[colorIndex]}`}
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />
                );
            }
        }
        
        return tiles;
    };

    // Calculate border dimensions
    const calculateBorders = () => {
        if (!quiltDimensions.quiltWidth || !quiltDimensions.quiltHeight || 
            !quiltDimensions.tileWidth || !quiltDimensions.tileHeight) {
            return { leftoverWidth: 0, leftoverHeight: 0 };
        }

        const widthInches = quiltDimensions.quiltWidth * 12;
        const heightInches = quiltDimensions.quiltHeight * 12;
        
        const usedWidth = quiltDimensions.columns * quiltDimensions.tileWidth;
        const usedHeight = quiltDimensions.rows * quiltDimensions.tileHeight;
        
        const leftoverWidth = widthInches - usedWidth;
        const leftoverHeight = heightInches - usedHeight;
        
        return { leftoverWidth, leftoverHeight };
    };
    
    const { leftoverWidth, leftoverHeight } = calculateBorders();
    
    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faRuler} className="mr-2" /> 
                Choose Your Quilt Dimensions
            </h2>
            
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base text-center">
                Your quilt dimensions determine how many clothing items you'll need and how your final memory quilt will look.
            </p>
            
            <div className="flex flex-col items-center md:grid md:grid-cols-2 gap-6 md:gap-8">
                <div className="w-full max-w-sm mx-auto mb-6 md:mb-8">
                    {/* Use QuiltSetup instead of custom dimensions layout */}
                    <QuiltSetup 
                        onDimensionsChange={handleDimensionsChange}
                        onGridSizeChange={() => {}}
                        defaultGridSize={gridSize}
                        disabled={generated || uploadedImages.length > 0}
                        initialDimensions={quiltDimensions}
                    />
                </div>
                
                <div className="w-full max-w-sm mx-auto space-y-4 sm:space-y-6">
                    {/* Quilt preview */}
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 text-center">Quilt Preview</h3>
                        <div className="flex justify-center">
                            <div className="border-2 border-gray-400 rounded-md p-2 bg-white">
                                {isQuiltDimensionsValid() && quiltDimensions.columns > 0 && quiltDimensions.rows > 0 ? (
                                    <div className="relative">
                                        {/* Container with proper aspect ratio for the quilt */}
                                        <div 
                                            style={{
                                                width: '280px', 
                                                height: `${280 * (quiltDimensions.quiltHeight / quiltDimensions.quiltWidth)}px`,
                                                maxHeight: '350px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                backgroundColor: '#f8f8f8',
                                                border: '1px solid #ddd'
                                            }}
                                        >
                                            {/* Center the grid in the container */}
                                            <div 
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${quiltDimensions.columns}, 1fr)`,
                                                    gridTemplateRows: `repeat(${quiltDimensions.rows}, 1fr)`,
                                                    gap: '2px',
                                                    width: `${((quiltDimensions.columns * quiltDimensions.tileWidth) / (quiltDimensions.quiltWidth * 12)) * 100}%`,
                                                    height: `${((quiltDimensions.rows * quiltDimensions.tileHeight) / (quiltDimensions.quiltHeight * 12)) * 100}%`,
                                                    position: 'absolute',
                                                    top: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                    left: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`
                                                }}
                                            >
                                                {generateTileColors()}
                                            </div>
                                            
                                            {/* Visual indicators for unused space - distributed evenly */}
                                            {leftoverWidth > 0 && (
                                                <>
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            left: '0',
                                                            top: '0',
                                                            width: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`,
                                                            height: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderRight: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            right: '0',
                                                            top: '0',
                                                            width: `${(leftoverWidth / (quiltDimensions.quiltWidth * 12) / 2) * 100}%`,
                                                            height: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderLeft: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                </>
                                            )}
                                            {leftoverHeight > 0 && (
                                                <>
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            top: '0',
                                                            left: '0',
                                                            height: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                            width: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderBottom: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                    <div 
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '0',
                                                            left: '0',
                                                            height: `${(leftoverHeight / (quiltDimensions.quiltHeight * 12) / 2) * 100}%`,
                                                            width: '100%',
                                                            backgroundColor: 'rgba(255, 220, 200, 0.7)',
                                                            borderTop: '2px dashed #ff6b6b'
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-sm p-8 text-center">Enter valid dimensions to see preview</div>
                                )}
                            </div>
                        </div>
                        <div className="text-center mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
                            <p>This preview maintains the proper proportions of your quilt and tiles</p>
                            {(leftoverWidth > 0 || leftoverHeight > 0) && isQuiltDimensionsValid() && (
                                <>
                                    <p className="text-amber-600 mt-1">Highlighted areas show unused space (distributed evenly)</p>
                                    <p className="text-green-600 mt-1">TIP: Consider using this space for decorative borders</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Layout Results Summary */}
            {isQuiltDimensionsValid() && (
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 mb-4 sm:mb-6 mt-4 w-full max-w-3xl mx-auto">
                    <h3 className="font-bold text-base sm:text-lg mb-2 text-[#9CAF88] text-center">Layout Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                        <div className="bg-white p-3 rounded border">
                            <div className="text-sm text-gray-500">Grid Size</div>
                            <div className="font-bold text-lg">{gridSize}</div>
                        </div>
                        <div className="bg-white p-3 rounded border">
                            <div className="text-sm text-gray-500">Required Images</div>
                            <div className="font-bold text-lg">{requiredImages}</div>
                        </div>
                        <div className="bg-white p-3 rounded border">
                            <div className="text-sm text-gray-500">Quilt Size</div>
                            <div className="font-bold">
                                {quiltDimensions.quiltWidth}' × {quiltDimensions.quiltHeight}'
                            </div>
                        </div>
                        <div className="bg-white p-3 rounded border">
                            <div className="text-sm text-gray-500">Tile Size</div>
                            <div className="font-bold">
                                {Number(quiltDimensions.tileWidth).toFixed(1)}" × {Number(quiltDimensions.tileHeight).toFixed(1)}"
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Warning message */}
            {showWarning && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 mb-4 sm:mb-6 w-full max-w-3xl mx-auto">
                    <p className="text-yellow-700 text-sm">{showWarning}</p>
                </div>
            )}
            
            {/* Continue button - Updated to show different text based on upload status */}
            <div className="flex justify-center sm:justify-end mt-4 sm:mt-6">
                <button
                    onClick={() => {
                        // Double-check validation before proceeding
                        if (isQuiltDimensionsValid()) {
                            nextStep();
                        } else {
                            // Show a warning if somehow the button is enabled but validation fails
                            setShowWarning("Please set valid quilt dimensions first");
                        }
                    }}
                    disabled={!isQuiltDimensionsValid()}
                    className={`w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center ${
                        isQuiltDimensionsValid()
                            ? 'bg-[#9CAF88] hover:bg-[#8A9B79] text-white'
                            : 'bg-gray-300 cursor-not-allowed text-gray-500'
                    }`}
                >
                    {allImagesUploaded ? (
                        <>View Uploaded Clothes <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></>
                    ) : (
                        <>Continue to Upload <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></>
                    )}
                </button>
            </div>
        </div>
    );
};

export default Step1;